<template>
  <div class="absolute inset-0 bg-white z-10 flex flex-col">
    <div style="border-color: #E6EBEF;" class="h-20 border-b px-6 flex justify-between items-center">
      <div class="flex items-center">
        <button class="w-6 h-6" @click="$emit('goBack')">
          <img width="24" height="24" :src="require('@/assets/images/meeting-note/arrow_left_gray.svg')" class="pointer-events-none" />
        </button>
        <span style="color: #2E3842;" class="ml-2 text-2xl font-bold">{{meetingNote.title}}</span>
      </div>
      <div class="flex items-center gap-x-2">
        <div v-if="!isMeetingNoteCompleted" class="flex items-center gap-x-2">
          <span class="text-red-400 leading-8">{{
            meetingNoteDurationForm
          }}</span>
          <button
            class="cursor-pointer rounded-full px-6 bg-red-50 px-4"
            @click.stop="onClickMeetingNoteStop(meetingNote)"
          >
            <span class="text-red-500 font-bold leading-9">{{ this.$t('extendMenu.meetingNote.summary.stop') }}</span>
          </button>
        </div>
        <button class="w-6 h-6" @click="$emit('close')">
          <img :src="require('@/assets/images/x_gray.svg')" />
        </button>
      </div>
    </div>
    <div
      class="h-16 flex"
      style="box-shadow: 0px 14px 28px -7px rgba(0, 0, 0, 0.04)"
    >
      <button
        class="flex-1 text-lg font-bold"
        :class="{ selected: selectedStt }"
        @click="() => select('stt')"
      >
        {{ this.$t('extendMenu.meetingNote.title') }}
      </button>
      <button
        class="flex-1 text-lg font-bold"
        v-if="isSummaryCompleted"
        :class="{ selected: !selectedStt }"
        @click="() => select('aiSummary')"
      >
        {{ this.$t('extendMenu.meetingNote.summary.title') }}
      </button>
    </div>
    <div class="min-h-0 flex-1 p-6 flex flex-col gap-x-2">
      <div class="flex justify-end gap-x-2">
        <RoomMeetingNoteButton
          v-if="selectedStt"
          @click="onClickMeetingNoteDownload(meetingNote)"
          >{{ this.$t('extendMenu.meetingNote.download.title') }}</RoomMeetingNoteButton
        >
        <RoomMeetingNoteButton
          color="navy"
          :download="false"
          v-if="selectedStt && isMeetingNoteCompleted"
          :class="{
            'cursor-not-allowed': isSummaryPending || isSummaryCompleted,
          }"
          @click="onClickRequsetSummary"
          >{{ this.$t('extendMenu.meetingNote.ai.summary.request') }}
          <div v-if="isSummaryPending" class="pl-2">
            <GrmLoadingCircle v-if="true" size="xs" />
          </div>
        </RoomMeetingNoteButton>
        <div class="relative">
          <RoomMeetingNoteButton
            v-if="selectedAiSummary && isSummaryCompleted"
            @click="onToggleIsOpenDownloadSummary(meetingNote)"
            >{{ this.$t('extendMenu.meetingNote.ai.summary.download.title') }}</RoomMeetingNoteButton
          >
          <div class="absolute z-50">
            <GrmMenu :list="summaryDownloadList" :open.sync="isOpenDownloadList" />
          </div>
        </div>
        <div class="relative">
          <RoomMeetingNoteButton
            v-if="selectedAiSummary && isSummaryCompleted"
            @click="onToggleIsOpenTranslationList"
            color="navy"
            :download="false"
            >{{ this.$t('extendMenu.meetingNote.ai.summary.translation') }}</RoomMeetingNoteButton
          >
          <div class="absolute z-50">
            <GrmMenu :list="translationLangList" :open.sync="isOpenTranslationList" />
          </div>
        </div>
      </div>
      <perfect-scrollbar class="min-h-0 flex-1">
        <div v-if="selectedStt" class="relative">
          <div class="mt-6" v-for="(stt, i) in meetingNote.stt" :key="i">
            <div class="space-x-1">
              <span
                class="font-bold"
                :style="{ color: isMe(stt.userId) ? '#015ECB' : '' }"
              >
                {{ getUsername(stt.userId) }}</span
              >
              <time>{{ formattedDate(stt.date || stt.regDate) }}</time>
            </div>
            <div class="mt-2" :class="{ 'font-bold': isMe(stt.userId) }">
              {{ stt.text }}
            </div>
          </div>
        </div>
        <div class="mt-10" v-else-if="isSummaryCompleted" ref="summaryRef">
          <div v-if="translationCompleted">
            <p class="font-bold mb-3">Translation Content</p>
            <pre style="word-break: keep-all" class="whitespace-pre-wrap">{{
              translatedSummary
              }}</pre>
          </div>
          <div v-else>
            <p class="font-bold mb-3">{{$t('extendMenu.meetingNote.translationContent')}}</p>
            <pre style="word-break: keep-all" class="whitespace-pre-wrap">{{
              meetingNote.summary
            }}</pre>
          </div>
        </div>
        <div>
          <vue-html2pdf
              :show-layout="false"
              :enable-download="true"
              :preview-modal="false"
              :manual-pagination="false"
              :paginate-elements-by-height="3000"
              pdf-format="b4"
              filename="ai_summary"
              @hasDownloaded="onDownloadPdf"
              ref="html2Pdf"
          >
            <template #pdf-content>
              <div class="pdf-download-wrapper">
                <p>{{`${$t("extendMenu.meetingNote.meetingDate")}: ${getFormedDate(meetingNote.startDate)} ~ ${getFormedTime(meetingNote.endDate)}`}}</p>
                <p>{{`${$t("extendMenu.meetingNote.writer")}: ${getUsername(meetingNote.userId) || $t("extendMenu.meetingNote.leavedUser")}`}}
                </p>
                <p>{{`${$t("extendMenu.meetingNote.summary.content")}`}}</p>
                <div v-if="translationCompleted">
                  <pre style="word-break: keep-all" class="whitespace-pre-wrap">{{
                      translatedSummary
                    }}</pre>
                </div>
                <div v-else>
                  <pre style="word-break: keep-all" class="whitespace-pre-wrap">{{
                      meetingNote.summary
                    }}</pre>
                </div>
              </div>
            </template>
          </vue-html2pdf>
        </div>
        <div ref="translatingLoadingRef" class="opacity-80 absolute left-0 is-translating" v-if="isTranslating">{{`${this.$t("extendMenu.meetingNote.summary.translation.progress")}`}}</div>
      </perfect-scrollbar>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import VueHtml2pdf from "vue-html2pdf"

import RoomMeetingNoteButton from "@/components/Room/MeetingNote/Button.vue";
import GrmLoadingCircle from "@/components/Common/loading/GrmLoadingCircle.vue";
import GrmMenu from "@/components/Common/menu/GrmMenu.vue";

export default {
  name: "GrmMeetingNoteDetail",
  components: {
    GrmMenu,
    RoomMeetingNoteButton,
    GrmLoadingCircle,
    VueHtml2pdf
  },
  watch: {
    selected: {
      immediate: false,
      handler(tab) {
        if (
          !this.selectedStt &&
          this.isSummaryCompleted &&
          !this.meetingNote.summary
        ) {
          this.getMeetingNoteSummary(this.meetingNote);
        }
        this.translatedSummary = ''
        this.translationCompleted = false
      },
    },
    isTranslating: {
      immediate: false,
      handler(){
        if(!this.isTranslating)
          return

        this.$nextTick(() => {
          const summaryRef = this.$refs.summaryRef
          const height = summaryRef.offsetHeight;
          const translatingLoadingRef = this.$refs.translatingLoadingRef;
          translatingLoadingRef.style.height = `${height}px`;
        })
      }
    }
  },
  props: {
    meetingNote: { default: null, type: Object },
  },
  data() {
    return {
      selected: "stt", //aiSummary
      isOpenTranslationList: false,
      isOpenDownloadList: false,
      translationCompleted: false,
      translatedSummary: '',
      isTranslating: false,
      pdfDown: false,
      htmlToPdfOptions: {},
      summaryDownloadList: [
        {
          title: this.$t('extendMenu.meetingNote.ai.summary.download.text'),
          key: "text",
          click: () => {
            this.onClickSummaryDownload(this.meetingNote)
          },
        },
        {
          title: this.$t('extendMenu.meetingNote.ai.summary.download.pdf'),
          key: "pdf",
          click: () => {
            this.onDownloadPdf();
          },
        },
      ],
      translationLangList: [
        {
          title: "한국어",
          key: "KO",
          icon: `translation_KO.png`,
          click: () => {
            this.onClickMeetingNoteTranslation(this.meetingNote, "KO");
          },
        },
        {
          title: "عربي",
          key: "AR",
          icon: "translation_AR.png",
          click: () => {
            this.onClickMeetingNoteTranslation(this.meetingNote, "AR");
          },
        },
        {
          title: "English",
          key: "EN",
          icon: "translation_EN.png",
          click: () => {
            this.onClickMeetingNoteTranslation(this.meetingNote, "EN");
          },
        },
        {
          title: "español",
          key: "ES",
          icon: "translation_ES.png",
          click: () => {
            this.onClickMeetingNoteTranslation(this.meetingNote, "ES");
          },
        },
        {
          title: "Tiếng Việt",
          key: "VI",
          icon: "translation_VI.png",
          click: () => {
            this.onClickMeetingNoteTranslation(this.meetingNote, "VI");
          },
        },
        {
          title: "日本語",
          key: "JA",
          icon: "translation_JA.png",
          click: () => {
            this.onClickMeetingNoteTranslation(this.meetingNote, "JA");
          },
        },
        {
          title: "簡體字",
          key: "ZH-CN",
          icon: "translation_ZH-CN.png",
          click: () => {
            this.onClickMeetingNoteTranslation(this.meetingNote, "ZH-CN");
          },
        },
      ],
    };
  },
  created() {
    this.$roomApi.addEventListeners((this._Events = _Events()), this);
  },
  destroyed() {
    this.$roomApi.removeEventListeners(this._Events)
  },
  computed: {
    ...mapGetters("user", ["me", "isMe", "getUsername"]),
    ...mapGetters("meetingNote", [
      "writingTime",
      "ongoingMeetingNote",
      "findMeetingNote",
    ]),
    selectedStt() {
      return this.selected === "stt";
    },
    selectedAiSummary() {
      return this.selected === "aiSummary";
    },
    formattedDate() {
      return (date) => {
        return date && moment(date).format("HH:mm:ss");
      };
    },
    getFormedDate() {
      return (date) => {
        return moment(date).format("YYYY-MM-DD HH:mm");
      };
    },
    getFormedTime() {
      return (date) => {
        return moment(date).format("HH:mm");
      };
    },
    showDownloadButton() {
      if (this.selectedStt) {
        return this.meetingNote.endDate;
      } else {
        return this.meetingNote.summaryState === "completed";
      }
    },
    isMeetingNoteCompleted() {
      return !!this.meetingNote.endDate;
    },
    isSummaryCompleted() {
      return this.meetingNote.summaryState === "completed";
    },
    isSummaryNotStarted() {
      return this.meetingNote.summaryState === "notStarted";
    },
    isSummaryFailed() {
      return this.meetingNote.summaryState === "failed";
    },
    isSummaryPending() {
      return this.meetingNote.summaryState === "pending";
    },
    meetingNoteDurationForm() {
      let mo = moment.utc(0).millisecond(this.writingTime);
      let minutes = mo.hours() * 60 + mo.minutes();
      if (minutes < 10) {
        minutes = "0" + minutes;
      }
      return minutes + mo.format(":ss");
    },
  },
  methods: {
    ...mapActions("meetingNote", [
      "getMeetingNote",
      "stopMeetingNote",
      "summarizeMeetingNote",
      "getMeetingNoteSummary",
    ]),
    ...mapActions("translation", ["translationText"]),
    select(tab) {
      this.selected = tab;
    },
    onClickMeetingNoteStop(meetingNote) {
      this.$alert({
        text: this.$t("extendMenu.meetingNote.quit"),
        ok: () => {
          this.stopMeetingNote(this.meetingNote);
        },
      });
    },
    onToggleIsOpenTranslationList() {
      this.isOpenTranslationList = !this.isOpenTranslationList;
    },
    onToggleIsOpenDownloadSummary() {
      this.isOpenDownloadList = !this.isOpenDownloadList;
    },
    async onClickMeetingNoteTranslation(meetingNote, targetLang) {
      this.isTranslating = true
      let data = await this.getMeetingNoteSummary(meetingNote);
      await this.translationText({
        text: data.summary,
        sourceLang: "",
        targetLang: targetLang,
        eventName: 'add_meetingNote_summary_translation',
        eventPublish: true
      });
      this.isTranslating = false
    },
    async onClickMeetingNoteDownload(meetingNote) {
      if (
        this.ongoingMeetingNote?.meetingNoteId === meetingNote.meetingNoteId
      ) {
        return this.$alert({
          text: this.$t("extendMenu.meetingNote.ongoing.download"),
          onlyOk: true,
        });
      } else {
        let data = await this.getMeetingNote(meetingNote);

        // TODO 회의록 원본 파일 생성 로직 - 데모용 임시
        const rows = data.map((row) => ({
          name:
            this.getUsername(row.userId) ||
            this.$t("extendMenu.notice.leavedUser"),
          regDate: row.regDate,
          duration: row.duration,
          text: row.text,
        }));

        const XLSX = await import("xlsx");
        const workSheet = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.sheet_add_aoa(
          workSheet,
          [
            [
              this.$t("common.name"),
              this.$t("extendMenu.meetingNote.regDate"),
              this.$t("extendMenu.meetingNote.speakTime"),
              this.$t("extendMenu.meetingNote.content"),
            ],
          ],
          { origin: "A1" }
        );

        workSheet["!cols"] = [
          { wch: 20 },
          { wch: 18 },
          { wch: 5 },
          {
            wch: 100,
            alignment: {
              vertical: "center",
              horizontal: "center",
              wrapText: true, // any truthy value here
            },
          },
        ];

        const workBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          workBook,
          workSheet,
          this.$t("extendMenu.meetingNote.title")
        );

        XLSX.writeFile(
          workBook,
          `${moment(meetingNote.startDate).format("YYMMDD_hhmm")}_${this.$t(
            "extendMenu.meetingNote.title"
          )}.xlsx`,
          { compression: true }
        );
        return;
      }
    },
    onClickRequsetSummary() {
      if (!this.isMeetingNoteCompleted) {
        return this.$alert({
          text: this.$t("extendMenu.meetingNote.ongoing.summary"),
          onlyOk: true,
        });
      } else {
        let alertText = "오류가 발생하였습니다. 관리자에게 문의해주세요.";
        switch (this.meetingNote.summaryState) {
          case "failed":
          case "notStarted":
            return this.$alert({
              text: this.$t("extendMenu.meetingNote.summary.alert.start"),
              ok: () => {
                this.summarizeMeetingNote(this.meetingNote);
              },
            });
          case "pending":
            return;
          case "completed":
            alertText = this.$t(
              "extendMenu.meetingNote.summary.alert.complete"
            );
            break;
        }
        return this.$alert({
          text: alertText,
          onlyOk: true,
        });
      }
    },
    async onClickSummaryDownload(meetingNote) {
      if (meetingNote.summaryState === "completed") {
        let data = await this.getMeetingNoteSummary(meetingNote);

        // TODO 데모용 회의록 요약 파일 생성 로직
        const messages = [
          `${this.$t(
            "extendMenu.meetingNote.meetingDate"
          )}: ${this.getFormedDate(data.startDate)} ~ ${this.getFormedTime(
            data.endDate
          )}\n`,
          `${this.$t("extendMenu.meetingNote.writer")}: ${
            this.getUsername(data.userId) ||
            this.$t("extendMenu.meetingNote.leavedUser")
          }\n`,
          `${this.$t("extendMenu.meetingNote.summary.content")}: ${
            this.translationCompleted && this.translatedSummary
                ? this.translatedSummary
                : data.summary
          }`,
        ];
        const blob = new Blob(messages, {
          type: "text/plain",
          endings: "native",
        });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        try {
          a.href = url;
          a.download = `${moment(meetingNote.startDate).format(
            "YYMMDD_hhmm"
          )}_${this.$t("extendMenu.meetingNote.summaryNote")}.txt`;
          document.body.append(a);
          a.click();
        } finally {
          window.URL.revokeObjectURL(url);
          a.remove();
        }

        return;
      }

      let alertText;
      switch (meetingNote.summaryState) {
        case "failed":
          alertText = this.$t("extendMenu.meetingNote.download.fail");
          break;
        case "notStarted":
          alertText = this.$t("extendMenu.meetingNote.download.notStarted");
          break;
        case "pending":
          alertText = this.$t("extendMenu.meetingNote.download.pending");
          break;
      }
      return this.$alert({
        text: alertText,
        onlyOk: true,
      });
    },
    onDownloadPdf() {
      this.pdfDown = !this.pdfDown;
      if (this.pdfDown) {
        this.$nextTick(() => {
          this.$refs.html2Pdf.generatePdf();
        });
      }
    }
  },
};

function _Events(){
  return {
    add_meetingNote_summary_translation(e) {
      const {
        data: {
          translateData: { translatedText },
        },
        userId
      } = e
      if (!this.isMe(userId)) {
        return
      }
      this.translationCompleted = true
      this.translatedSummary = translatedText
      this.isTranslating = false
    },
    add_meetingNote_summary_translation_error(e) {
      const {
        data: {
          translateErrorData: { errType, errCode, errMsg },
        },
        userId
      } = e
      if (!this.isMe(userId)) {
        return
      }
      console.warn(`## meetingNote summary translation failed -> errCode[${errCode}], errMsg[${errMsg}], errType[${errType}]`)
      this.$alert({
        text: `${this.$t('extendMenu.meetingNote.summary.translation.event.failed')}<br/>[${errMsg}]`,
        ok: () => {
          this.isTranslating = false
        },
      })
    },
  }
}
</script>

<style lang="scss" scoped>
.selected {
  color: #2e3842;
  font-weight: bold;
  border-bottom: grey 3px solid;
}
.note-detail-container {
  width: 650px;
  left: 100%;
  transform: translateX(5px);
}
.is-translating{
  background: white;
  width: 100%;

  top: 2.5rem;

  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 20px;
}
.pdf-download-wrapper {
  margin: 20px 30px;
}
</style>
